import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';
import { MemberInsightHistory } from '../../components/ui/MemberInsightHistory';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  ArrowLeft, CreditCard, Calendar, Activity, ChevronLeft, ChevronRight,
  Plus, Edit2, FileText, Clock, DollarSign, CheckCircle, Loader2, Trash2
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

// Exercise code → 한글 라벨 매핑 (CRM/App 동기화)
const EXERCISE_LABELS = {
  deadlift: '데드리프트',
  squat: '스쿼트',
  bench_press: '벤치프레스',
  shoulder_press: '숄더프레스',
  pushup: '푸시업',
  situp: '싯업',
  barbell_squat_endurance: '바벨 스쿼트',
  barbell_deadlift_endurance: '바벨 데드리프트',
  barbell_shoulder_press_endurance: '바벨 숄더프레스',
  plank: '플랭크',
  wall_squat: '월 스쿼트',
  amrap1: 'AMRAP1',
  amrap2: 'AMRAP2',
  amrap3: 'AMRAP3',
  amrap4: 'AMRAP4',
  running: '러닝',
  rowing: '로잉',
  inbody: '인바디',
  posture: '자세평가',
};
const getExerciseLabel = (code) => EXERCISE_LABELS[code] || code || '-';

// 측정 세부 정보 - raw_metrics + computed_metrics 기반 행 배열 생성
const buildMetricRows = (m) => {
  if (!m) return [];
  const rm = m.raw_metrics || {};
  const cm = m.computed_metrics || {};
  const rows = [];

  if (rm.weight_kg !== undefined && m.category !== 'inbody') rows.push(['무게', `${rm.weight_kg} kg`]);
  if (rm.reps !== undefined) rows.push(['반복 횟수', `${rm.reps} 회`]);
  if (rm.input_load_kg !== undefined) rows.push(['사용 중량', `${rm.input_load_kg} kg`]);
  if (rm.seconds !== undefined) rows.push(['유지 시간', `${rm.seconds} 초`]);
  if (rm.distance_m !== undefined) rows.push(['거리', `${rm.distance_m} m`]);
  if (rm.duration_seconds !== undefined) {
    const mins = Math.floor(rm.duration_seconds / 60);
    const secs = rm.duration_seconds % 60;
    rows.push(['시간', `${mins}분 ${secs}초`]);
  }
  // 인바디
  if (m.category === 'inbody' && rm.weight_kg !== undefined) rows.push(['체중', `${rm.weight_kg} kg`]);
  if (rm.body_fat_pct !== undefined) rows.push(['체지방률', `${rm.body_fat_pct} %`]);
  if (rm.skeletal_muscle_kg !== undefined) rows.push(['골격근량', `${rm.skeletal_muscle_kg} kg`]);
  if (rm.inbody_score !== undefined) rows.push(['인바디 점수', `${rm.inbody_score}`]);
  // 자세
  if (rm.posture_score !== undefined) rows.push(['자세 점수', `${rm.posture_score}`]);
  if (rm.memo) rows.push(['메모', rm.memo]);

  // Computed metrics
  if (cm.one_rm !== undefined) rows.push(['1RM 추정', `${cm.one_rm} kg`]);
  if (cm.relative_strength !== undefined) rows.push(['상대 근력', cm.relative_strength.toFixed(2)]);
  if (cm.vo2 !== undefined) rows.push(['VO2max', `${cm.vo2}`]);
  if (cm.base_load_kg !== undefined) {
    const diff = cm.load_diff_pct;
    const diffStr = diff !== undefined ? ` (${diff > 0 ? '+' : ''}${diff}%)` : '';
    rows.push(['기준 중량', `${cm.base_load_kg} kg${diffStr}`]);
  }

  return rows;
};

const MemberDetail = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('passes');
  
  // Edit member state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '', phone: '', email: '', gender: '', birth_date: '', body_weight_kg: '', actual_start_date: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  
  // Calendar state for reservation tab
  const [calendarDate, setCalendarDate] = useState(new Date());
  
  // Period selection for fitness score
  const [scorePeriod, setScorePeriod] = useState('12');

  // 점수 레벨 정책 (기준표 관리에서 운영자가 편집 가능)
  const [scoreLevels, setScoreLevels] = useState([]);
  useEffect(() => {
    let mounted = true;
    apiClient.get('/admin/score-levels')
      .then((r) => { if (mounted) setScoreLevels(r.data?.levels || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);
  
  // Measurement filter state
  const [measurementFilter, setMeasurementFilter] = useState('all');
  
  // Measurement deletion state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, measurement: null });
  const [deletingMeasurement, setDeletingMeasurement] = useState(false);
  
  // Fitness score tab state
  const [scoreDataTab, setScoreDataTab] = useState('exercise'); // 'exercise' | 'measurement'
  const [selectedChartPoint, setSelectedChartPoint] = useState(null); // { category, data }
  
  // 연령 평균값 (API에서 로드)
  const [averageScores, setAverageScores] = useState({
    strength: null,
    muscular_endurance: null,
    cardio: null,
    body_composition: null,
    balance: null
  });
  const [showAverageOnChart, setShowAverageOnChart] = useState(false);
  
  // Check if any average score is set
  const hasAverageData = useMemo(() => {
    return Object.values(averageScores).some(v => v !== null && v !== undefined);
  }, [averageScores]);
  
  // Memo state
  const [memoText, setMemoText] = useState('');
  const [memoLoading, setMemoLoading] = useState(false);
  
  // Pass registration state
  const [passDialogOpen, setPassDialogOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [passForm, setPassForm] = useState({
    pass_type: 'period',
    name: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    total_count: 10,
    duration_days: 30,
    price: '',
  });
  const [endDateManuallyEdited, setEndDateManuallyEdited] = useState(false);
  const [passLoading, setPassLoading] = useState(false);
  const [passSubTab, setPassSubTab] = useState('active');

  // Pass edit state
  const [editPassDialogOpen, setEditPassDialogOpen] = useState(false);
  const [editPassForm, setEditPassForm] = useState({
    pass_id: '', pass_type: '', name: '',
    start_date: '', end_date: '', total_count: 0, remaining_count: 0
  });
  const [editPassLoading, setEditPassLoading] = useState(false);

  // Resume holding state
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [resumePassId, setResumePassId] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);

  // ========== Delete member state ==========
  const [deleteMemberDialogOpen, setDeleteMemberDialogOpen] = useState(false);
  const [deleteMemberSubmitting, setDeleteMemberSubmitting] = useState(false);

  const handleDeleteMember = async () => {
    if (!data?.member?._id) return;
    setDeleteMemberSubmitting(true);
    try {
      await apiClient.delete(`/admin/members/${data.member._id}`);
      toast.success('회원이 삭제되었습니다.');
      setDeleteMemberDialogOpen(false);
      navigate('/admin/members');
    } catch (error) {
      toast.error(error.response?.data?.detail || '회원 삭제 실패');
      setDeleteMemberSubmitting(false);
    }
  };

  // ========== Pass upgrade state ==========
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeSourcePass, setUpgradeSourcePass] = useState(null);
  const [upgradeOptions, setUpgradeOptions] = useState([]);
  const [upgradeCurrentMeta, setUpgradeCurrentMeta] = useState(null);
  const [upgradeSelectedProductId, setUpgradeSelectedProductId] = useState('');
  const [upgradeDiffPrice, setUpgradeDiffPrice] = useState('');
  const [upgradeOptionsLoading, setUpgradeOptionsLoading] = useState(false);
  const [upgradeSubmitting, setUpgradeSubmitting] = useState(false);

  const openUpgradeDialog = async (pass) => {
    setUpgradeSourcePass(pass);
    setUpgradeOptions([]);
    setUpgradeCurrentMeta(null);
    setUpgradeSelectedProductId('');
    setUpgradeDiffPrice('');
    setUpgradeDialogOpen(true);
    setUpgradeOptionsLoading(true);
    try {
      const res = await apiClient.get(`/admin/passes/${pass._id}/upgrade-options`);
      setUpgradeOptions(res.data.options || []);
      setUpgradeCurrentMeta(res.data.current || null);
      if ((res.data.options || []).length === 0) {
        toast.info('업그레이드 가능한 상위 상품이 없습니다');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || '업그레이드 옵션 조회 실패');
      setUpgradeDialogOpen(false);
    } finally {
      setUpgradeOptionsLoading(false);
    }
  };

  const selectedUpgradeTarget = useMemo(
    () => upgradeOptions.find((o) => o.product_id === upgradeSelectedProductId) || null,
    [upgradeOptions, upgradeSelectedProductId]
  );

  // Suggest default diff = target price - current price (회원이 직접 조정 가능)
  useEffect(() => {
    if (selectedUpgradeTarget && upgradeCurrentMeta) {
      const suggested = Math.max(0, (selectedUpgradeTarget.price || 0) - (upgradeCurrentMeta.price || 0));
      setUpgradeDiffPrice(String(suggested));
    }
  }, [upgradeSelectedProductId, selectedUpgradeTarget, upgradeCurrentMeta]);

  // Preview new end_date
  const upgradeNewEndDatePreview = useMemo(() => {
    if (!selectedUpgradeTarget || !upgradeCurrentMeta?.end_date) return null;
    const extDays = (selectedUpgradeTarget.duration_days || 0) - (upgradeCurrentMeta.duration_days || 0);
    if (extDays <= 0) return null;
    const d = new Date(upgradeCurrentMeta.end_date);
    d.setDate(d.getDate() + extDays);
    return { iso: d.toISOString().split('T')[0], extension_days: extDays };
  }, [selectedUpgradeTarget, upgradeCurrentMeta]);

  const handleSubmitUpgrade = async () => {
    if (!upgradeSourcePass) return;
    if (!upgradeSelectedProductId) {
      toast.error('변경할 상품을 선택해주세요');
      return;
    }
    const diff = Number(upgradeDiffPrice);
    if (Number.isNaN(diff) || diff < 0) {
      toast.error('차액은 0 이상의 숫자여야 합니다');
      return;
    }
    setUpgradeSubmitting(true);
    try {
      await apiClient.post(`/admin/passes/${upgradeSourcePass._id}/upgrade`, {
        new_product_id: upgradeSelectedProductId,
        diff_price: diff,
      });
      toast.success('이용권이 업그레이드되었습니다');
      setUpgradeDialogOpen(false);
      fetchMemberDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || '업그레이드 실패');
    } finally {
      setUpgradeSubmitting(false);
    }
  };

  useEffect(() => {
    fetchMemberDetail();
    fetchProducts();
    fetchAgeAverages();
  }, [memberId]);

  const fetchAgeAverages = async () => {
    try {
      const response = await apiClient.get('/admin/age-average-scores');
      setAverageScores({
        strength: response.data.strength,
        muscular_endurance: response.data.muscular_endurance,
        cardio: response.data.cardio,
        body_composition: response.data.body_composition,
        balance: response.data.balance
      });
    } catch (error) {
      console.error('Failed to fetch age averages:', error);
    }
  };

  const openEditPass = (pass) => {
    setEditPassForm({
      pass_id: pass._id,
      pass_type: pass.pass_type,
      name: pass.name,
      start_date: pass.start_date ? pass.start_date.split('T')[0] : '',
      end_date: pass.end_date ? pass.end_date.split('T')[0] : '',
      total_count: pass.total_count || 0,
      remaining_count: pass.remaining_count || 0,
    });
    setEditPassDialogOpen(true);
  };

  const handleEditPassSave = async () => {
    // Validation
    if (editPassForm.start_date > editPassForm.end_date) {
      toast.error('시작일이 종료일보다 늦을 수 없습니다');
      return;
    }
    if (editPassForm.pass_type === 'count') {
      if (editPassForm.total_count < 0) { toast.error('최초 횟수는 0 이상이어야 합니다'); return; }
      if (editPassForm.remaining_count < 0) { toast.error('잔여 횟수는 0 이상이어야 합니다'); return; }
      if (editPassForm.remaining_count > editPassForm.total_count) { toast.error('잔여 횟수가 최초 횟수보다 클 수 없습니다'); return; }
    }

    setEditPassLoading(true);
    try {
      const payload = {
        start_date: editPassForm.start_date,
        end_date: editPassForm.end_date,
      };
      if (editPassForm.pass_type === 'count') {
        payload.total_count = Number(editPassForm.total_count);
        payload.remaining_count = Number(editPassForm.remaining_count);
      }
      await apiClient.put(`/admin/passes/${editPassForm.pass_id}`, payload);
      toast.success('이용권이 수정되었습니다');
      setEditPassDialogOpen(false);
      fetchMemberDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || '이용권 수정 실패');
    } finally {
      setEditPassLoading(false);
    }
  };


  // ========== Refund pass state + handler ==========
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundPass, setRefundPass] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  const openRefundDialog = (pass) => {
    setRefundPass(pass);
    setRefundAmount(pass.price ? String(pass.price) : '');
    setRefundReason('');
    setRefundDialogOpen(true);
  };

  const handleRefundPass = async () => {
    if (!refundPass) return;
    const amt = Number(refundAmount);
    if (!amt || amt <= 0) {
      toast.error('환불 금액을 올바르게 입력해주세요');
      return;
    }
    setRefundLoading(true);
    try {
      await apiClient.post(`/admin/passes/${refundPass._id}/refund`, {
        amount: amt,
        reason: refundReason.trim() || undefined,
      });
      toast.success('환불 처리가 완료되었습니다');
      setRefundDialogOpen(false);
      fetchMemberDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || '환불 처리 실패');
    } finally {
      setRefundLoading(false);
    }
  };


  const handleResumePass = async () => {
    if (!resumePassId) return;
    setResumeLoading(true);
    try {
      await apiClient.post(`/admin/passes/${resumePassId}/resume`);
      toast.success('이용권이 오늘부터 재개되었습니다');
      setResumeDialogOpen(false);
      setResumePassId(null);
      fetchMemberDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || '홀딩 재개 실패');
    } finally {
      setResumeLoading(false);
    }
  };


  const fetchProducts = async () => {
    try {
      const response = await apiClient.get('/admin/products?is_active=true');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchMemberDetail = async () => {
    try {
      const response = await apiClient.get(`/admin/members/${memberId}`);
      setData(response.data);
      // Initialize edit form and memo
      const m = response.data.member;
      setEditForm({
        name: m.name || '',
        phone: m.phone || '',
        email: m.email || '',
        gender: m.gender || '',
        birth_date: m.birth_date ? m.birth_date.split('T')[0] : '',
        body_weight_kg: m.body_weight_kg || '',
        actual_start_date: m.actual_start_date ? m.actual_start_date.split('T')[0] : ''
      });
      setMemoText(m.memo || '');
    } catch (error) {
      console.error('Failed to fetch member detail:', error);
      toast.error('회원 정보 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  // 측정 데이터 삭제
  const handleDeleteMeasurement = async () => {
    const m = deleteDialog.measurement;
    if (!m) return;
    setDeletingMeasurement(true);
    try {
      await apiClient.delete(`/admin/measurements/${m._id}`);
      toast.success('측정 데이터가 삭제되었습니다');
      setDeleteDialog({ open: false, measurement: null });
      await fetchMemberDetail();
    } catch (error) {
      console.error('Failed to delete measurement:', error);
      toast.error('삭제 실패: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingMeasurement(false);
    }
  };

  // Calculate age from birth_date
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Format date
  // 한국 서비스 기준 KST(Asia/Seoul) 기준으로 날짜를 추출
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const fmt = new Intl.DateTimeFormat('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric', month: '2-digit', day: '2-digit',
      });
      // "2026. 05. 20." → "2026.05.20"
      return fmt.format(d).replace(/\s/g, '').replace(/\.$/, '');
    } catch {
      return dateStr;
    }
  };

  // Format date for chart (YY.MM.DD)
  const formatChartDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return `${String(d.getFullYear()).slice(-2)}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    } catch {
      return dateStr;
    }
  };

  // 점수 등급 계산 — 기준표 관리(`/admin/score-levels`)의 동적 레벨 정책을 따른다.
  // 운영자가 레벨 이름/구간을 바꾸면 회원 앱과 CRM 양쪽에 동일하게 즉시 반영된다.
  // 컬러는 레벨 순서(낮→높)에 따른 팔레트로 자동 매핑되며 하드코딩된 등급명은 제거함.
  const LEVEL_COLOR_CLASSES = [
    'text-red-500',     // 0 (lowest)
    'text-amber-500',   // 1
    'text-green-600',   // 2
    'text-blue-600',    // 3
    'text-purple-600',  // 4
    'text-pink-600',    // 5
    'text-emerald-600', // 6
  ];
  const getScoreGrade = (score) => {
    if (score === null || score === undefined) return { label: '-', color: 'text-gray-400' };
    if (!Array.isArray(scoreLevels) || scoreLevels.length === 0) {
      return { label: '-', color: 'text-gray-400' };
    }
    // levels는 min_score 오름차순 — score 이상인 가장 높은 레벨을 선택
    let matchedIdx = 0;
    for (let i = 0; i < scoreLevels.length; i++) {
      if (score >= (scoreLevels[i].min_score ?? 0)) matchedIdx = i;
    }
    const lvl = scoreLevels[matchedIdx];
    return {
      label: lvl?.name || '-',
      color: LEVEL_COLOR_CLASSES[matchedIdx] || LEVEL_COLOR_CLASSES[LEVEL_COLOR_CLASSES.length - 1],
    };
  };

  // Handle member edit
  const handleEditMember = async () => {
    setEditLoading(true);
    try {
      const data = { ...editForm };
      if (data.body_weight_kg) {
        data.body_weight_kg = parseFloat(data.body_weight_kg);
      } else {
        data.body_weight_kg = null;
      }
      await apiClient.put(`/admin/members/${memberId}`, data);
      toast.success('회원 정보가 수정되었습니다');
      setEditDialogOpen(false);
      fetchMemberDetail();
    } catch (error) {
      console.error('Failed to update member:', error);
      toast.error('회원 정보 수정 실패');
    } finally {
      setEditLoading(false);
    }
  };

  // Handle memo save
  const handleSaveMemo = async () => {
    setMemoLoading(true);
    try {
      await apiClient.put(`/admin/members/${memberId}/memo`, { memo: memoText });
      toast.success('메모가 저장되었습니다');
    } catch (error) {
      console.error('Failed to save memo:', error);
      toast.error('메모 저장 실패');
    } finally {
      setMemoLoading(false);
    }
  };

  // Handle product selection - auto-fill form
  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
    
    if (!productId) {
      // 직접 입력 선택 시 폼 초기화
      setPassForm({
        pass_type: 'period',
        name: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        total_count: 10,
        duration_days: 30,
        price: '',
      });
      setEndDateManuallyEdited(false);
      return;
    }
    
    const product = products.find(p => p._id === productId);
    if (product) {
      const startDate = passForm.start_date || new Date().toISOString().split('T')[0];
      let endDate = '';
      
      // 기간제든 횟수제든 duration_days가 있으면 종료일 계산
      if (product.duration_days) {
        const start = new Date(startDate);
        start.setDate(start.getDate() + product.duration_days - 1);
        endDate = start.toISOString().split('T')[0];
      }
      
      setPassForm({
        pass_type: product.pass_type,
        name: product.name,
        start_date: startDate,
        end_date: endDate,
        total_count: product.total_count || 10,
        duration_days: product.duration_days || 30,
        price: product.price ?? '',
      });
      setEndDateManuallyEdited(false);
    }
  };

  // Handle start date change - recalculate end date (only if not manually edited)
  const handleStartDateChange = (newStartDate) => {
    const product = products.find(p => p._id === selectedProductId);
    let newEndDate = passForm.end_date;
    
    if (!endDateManuallyEdited && product && product.duration_days) {
      const start = new Date(newStartDate);
      start.setDate(start.getDate() + product.duration_days - 1);
      newEndDate = start.toISOString().split('T')[0];
    }
    
    setPassForm({
      ...passForm,
      start_date: newStartDate,
      end_date: newEndDate
    });
  };

  // Handle pass registration
  const handleCreatePass = async () => {
    if (!passForm.name) {
      toast.error('이용권 이름을 입력해주세요');
      return;
    }
    if (!passForm.start_date || !passForm.end_date) {
      toast.error('시작일과 종료일을 입력해주세요');
      return;
    }
    if (new Date(passForm.end_date) < new Date(passForm.start_date)) {
      toast.error('종료일은 시작일 이후여야 합니다');
      return;
    }

    setPassLoading(true);
    try {
      const payload = {
        member_id: memberId,
        pass_type: passForm.pass_type,
        name: passForm.name,
        start_date: passForm.start_date,
        end_date: passForm.end_date,
      };
      
      // 횟수제인 경우 횟수 추가
      if (passForm.pass_type === 'count') {
        payload.total_count = parseInt(passForm.total_count);
        payload.remaining_count = parseInt(passForm.total_count);
      }

      // 가격
      if (passForm.price !== '' && passForm.price !== null) {
        payload.price = parseInt(passForm.price);
      }
      
      await apiClient.post('/admin/passes', payload);
      toast.success('이용권이 등록되었습니다');
      setPassDialogOpen(false);
      // Reset form
      setSelectedProductId('');
      setPassForm({
        pass_type: 'period',
        name: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        total_count: 10,
        duration_days: 30,
        price: '',
      });
      setEndDateManuallyEdited(false);
      fetchMemberDetail();
    } catch (error) {
      console.error('Failed to create pass:', error);
      toast.error('이용권 등록 실패: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPassLoading(false);
    }
  };

  // Calendar navigation
  const prevMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
  };

  // Generate calendar days
  const getCalendarDays = useMemo(() => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday
    const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // Monday = 0
    
    const days = [];
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = adjustedStartDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthLastDay - i, isCurrentMonth: false });
    }
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ day: i, isCurrentMonth: true });
    }
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ day: i, isCurrentMonth: false });
    }
    return days;
  }, [calendarDate]);

  // Reservation stats for current month
  const reservationStats = useMemo(() => {
    if (!data?.reservations) return { total: 0, attended: 0, noShow: 0 };
    
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    
    const monthReservations = data.reservations.filter(r => {
      const date = new Date(r.session?.scheduled_at || r.created_at);
      return date.getFullYear() === year && date.getMonth() === month;
    });
    
    return {
      total: monthReservations.length,
      attended: monthReservations.filter(r => r.status === 'confirmed' || r.attendance_status === 'present').length,
      noShow: monthReservations.filter(r => r.status === 'no_show' || r.attendance_status === 'no_show').length
    };
  }, [data?.reservations, calendarDate]);

  // Prepare radar chart data - 회원 점수 + 평균 비교
  const radarData = useMemo(() => {
    const fs = data?.fitness_snapshot || {};
    return [
      { subject: '근력', score: fs.strength_score || 0, average: showAverageOnChart && averageScores.strength != null ? averageScores.strength : null, fullMark: 100 },
      { subject: '심폐지구력', score: fs.cardio_score || 0, average: showAverageOnChart && averageScores.cardio != null ? averageScores.cardio : null, fullMark: 100 },
      { subject: '신체 조성', score: fs.body_composition_score || 0, average: showAverageOnChart && averageScores.body_composition != null ? averageScores.body_composition : null, fullMark: 100 },
      { subject: '신체 균형', score: fs.balance_score || 0, average: showAverageOnChart && averageScores.balance != null ? averageScores.balance : null, fullMark: 100 },
      { subject: '근지구력', score: fs.muscular_endurance_score || 0, average: showAverageOnChart && averageScores.muscular_endurance != null ? averageScores.muscular_endurance : null, fullMark: 100 },
    ];
  }, [data?.fitness_snapshot, averageScores, showAverageOnChart]);

  // Prepare line chart data for score history - 개별 측정일 기준으로 표시 (누락 방지)
  const scoreHistoryData = useMemo(() => {
    if (!data?.measurements || data.measurements.length === 0) return [];
    
    // Filter only strength, muscular_endurance, cardio categories
    const filteredMeasurements = data.measurements.filter(m => 
      ['strength', 'muscular_endurance', 'cardio'].includes(m.category) && m.score
    );
    
    if (filteredMeasurements.length === 0) return [];
    
    // Group by date (YYYY-MM-DD) to preserve all individual measurements
    const dateMap = new Map();
    const sortedMeasurements = [...filteredMeasurements].sort((a, b) => 
      new Date(a.measured_at) - new Date(b.measured_at)
    );
    
    sortedMeasurements.forEach(m => {
      const date = new Date(m.measured_at);
      // 날짜 기준 키 생성 (YY.MM.DD 형식)
      const dateKey = `${String(date.getFullYear()).slice(-2)}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
      const fullKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      
      if (!dateMap.has(fullKey)) {
        dateMap.set(fullKey, { 
          date: dateKey, 
          fullDate: fullKey,
          strength: null, 
          endurance: null, 
          cardio: null,
          strengthData: null,
          enduranceData: null,
          cardioData: null
        });
      }
      
      const entry = dateMap.get(fullKey);
      if (m.category === 'strength') {
        entry.strength = m.score;
        entry.strengthData = m;
      }
      if (m.category === 'muscular_endurance') {
        entry.endurance = m.score;
        entry.enduranceData = m;
      }
      if (m.category === 'cardio') {
        entry.cardio = m.score;
        entry.cardioData = m;
      }
    });
    
    // 기간 필터링: scorePeriod가 'all'이면 전체, 아니면 해당 주 동안의 데이터만
    if (scorePeriod === 'all') {
      return Array.from(dateMap.values())
        .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
    }
    
    const now = new Date();
    const weeksAgo = new Date(now.getTime() - parseInt(scorePeriod) * 7 * 24 * 60 * 60 * 1000);
    
    return Array.from(dateMap.values())
      .filter(d => new Date(d.fullDate) >= weeksAgo)
      .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  }, [data?.measurements, scorePeriod]);
  
  // 측정 기록 데이터 (신체 조성, 신체 균형)
  const measurementHistoryData = useMemo(() => {
    if (!data?.measurements || data.measurements.length === 0) return [];
    
    const filteredMeasurements = data.measurements.filter(m => 
      ['inbody', 'posture'].includes(m.category) && m.score
    );
    
    if (filteredMeasurements.length === 0) return [];
    
    const dateMap = new Map();
    const sortedMeasurements = [...filteredMeasurements].sort((a, b) => 
      new Date(a.measured_at) - new Date(b.measured_at)
    );
    
    sortedMeasurements.forEach(m => {
      const date = new Date(m.measured_at);
      const dateKey = `${String(date.getFullYear()).slice(-2)}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
      const fullKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      
      if (!dateMap.has(fullKey)) {
        dateMap.set(fullKey, { 
          date: dateKey, 
          fullDate: fullKey,
          bodyComposition: null, 
          balance: null,
          bodyCompositionData: null,
          balanceData: null
        });
      }
      
      const entry = dateMap.get(fullKey);
      if (m.category === 'inbody') {
        entry.bodyComposition = m.score;
        entry.bodyCompositionData = m;
      }
      if (m.category === 'posture') {
        entry.balance = m.score;
        entry.balanceData = m;
      }
    });
    
    if (scorePeriod === 'all') {
      return Array.from(dateMap.values())
        .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
    }
    
    const now = new Date();
    const weeksAgo = new Date(now.getTime() - parseInt(scorePeriod) * 7 * 24 * 60 * 60 * 1000);
    
    return Array.from(dateMap.values())
      .filter(d => new Date(d.fullDate) >= weeksAgo)
      .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  }, [data?.measurements, scorePeriod]);
  
  // Get latest measurement by category for display
  const getLatestMeasurementByCategory = (category) => {
    if (!data?.measurements) return null;
    return data.measurements
      .filter(m => m.category === category)
      .sort((a, b) => new Date(b.measured_at) - new Date(a.measured_at))[0];
  };
  
  const latestStrength = useMemo(() => getLatestMeasurementByCategory('strength'), [data?.measurements]);
  const latestEndurance = useMemo(() => getLatestMeasurementByCategory('muscular_endurance'), [data?.measurements]);
  const latestCardio = useMemo(() => getLatestMeasurementByCategory('cardio'), [data?.measurements]);

  // Check if pass is currently in holding period
  const isCurrentlyHolding = (pass) => {
    if (!pass.holding_start || !pass.holding_end) return false;
    const today = new Date().toISOString().split('T')[0];
    return pass.holding_start <= today && today <= pass.holding_end;
  };

  // Calculate remaining holding days
  const getRemainingHoldingDays = (holdingEnd) => {
    const today = new Date();
    const end = new Date(holdingEnd);
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  // Separate active and expired passes
  // A count-based pass with 0 remaining is treated as expired regardless of period.
  const { activePasses, expiredPasses } = useMemo(() => {
    if (!data?.passes) return { activePasses: [], expiredPasses: [] };
    const today = new Date().toISOString().split('T')[0];
    const isExhausted = (p) => p.pass_type === 'count' && (p.remaining_count ?? 0) <= 0;

    const active = data.passes.filter(p => p.end_date >= today && p.status === 'active' && !isExhausted(p));
    const expired = data.passes.filter(p => p.end_date < today || p.status !== 'active' || isExhausted(p));

    return { activePasses: active, expiredPasses: expired };
  }, [data?.passes]);

  // Get holding history from passes
  const holdingHistory = useMemo(() => {
    if (!data?.passes) return [];
    
    const history = [];
    data.passes.forEach(pass => {
      if (pass.holding_start && pass.holding_end) {
        const today = new Date().toISOString().split('T')[0];
        const holdingDays = Math.max(0, Math.ceil(
          (new Date(pass.holding_end) - new Date(pass.holding_start)) / (1000 * 60 * 60 * 24)
        ) + 1);

        // Actual extension applied to the end_date (end_date - original_end_date)
        let extensionDays = holdingDays;
        if (pass.original_end_date) {
          extensionDays = Math.max(0, Math.round(
            (new Date(pass.end_date) - new Date(pass.original_end_date)) / (1000 * 60 * 60 * 24)
          ));
        }

        history.push({
          pass_name: pass.name,
          holding_start: pass.holding_start,
          holding_end: pass.holding_end,
          holding_days: holdingDays,
          original_end_date: pass.original_end_date || null,
          extended_end_date: pass.end_date,
          extension_days: extensionDays,
          status: pass.holding_end >= today ? '진행중' : '종료됨'
        });
      }
    });
    
    return history.sort((a, b) => new Date(b.holding_start) - new Date(a.holding_start));
  }, [data?.passes]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!data) {
    return <div className="p-8">회원을 찾을 수 없습니다.</div>;
  }

  const { member, passes, reservations, measurements, fitness_snapshot, attendances, transactions } = data;
  const memberAge = calculateAge(member.birth_date);
  const genderDisplay = member.gender === 'male' ? '남' : member.gender === 'female' ? '여' : '-';

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/admin/members')} className="mb-4">
        <ArrowLeft className="mr-2 h-4 w-4" />
        뒤로 가기
      </Button>

      {/* Header Section */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">{member.name}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-2">
            <span>회원번호: {member.member_number}</span>
            <span>전화: {member.phone}</span>
            {member.email && <span>이메일: {member.email}</span>}
          </div>
          <div className="flex gap-4 text-sm text-gray-600">
            <span>성별: {genderDisplay}</span>
            {memberAge && <span>나이: {memberAge}세</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={async () => {
              try {
                const res = await apiClient.post(`/admin/members/${member._id}/reset-pin`);
                toast.success(`PIN이 재설정되었습니다: ${res.data.default_pin}`);
              } catch (error) {
                toast.error('PIN 재설정 실패');
              }
            }}
          >
            PIN 재설정
          </Button>
          <Button onClick={() => setEditDialogOpen(true)} className="bg-gray-900 hover:bg-gray-800">
            <Plus className="mr-2 h-4 w-4" />
            정보 수정
          </Button>
          <Button
            variant="outline"
            onClick={() => setDeleteMemberDialogOpen(true)}
            className="text-red-600 border-red-300 hover:bg-red-50"
            data-testid="delete-member-btn"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            회원 삭제
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white border">
          <TabsTrigger value="passes">이용권</TabsTrigger>
          <TabsTrigger value="reservations">예약 내역</TabsTrigger>
          <TabsTrigger value="measurements">측정 데이터</TabsTrigger>
          <TabsTrigger value="score">피트니스 점수</TabsTrigger>
          <TabsTrigger value="payments">결제내역</TabsTrigger>
          <TabsTrigger value="attendance">출석 내역</TabsTrigger>
          <TabsTrigger value="memo">메모</TabsTrigger>
        </TabsList>

        {/* ========== 이용권 탭 ========== */}
        <TabsContent value="passes" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-4">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPassSubTab('active')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    passSubTab === 'active'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  data-testid="pass-sub-active"
                >
                  <CreditCard className="h-4 w-4" />
                  보유 이용권
                  {activePasses.length > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
                      passSubTab === 'active' ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>{activePasses.length}</span>
                  )}
                </button>
                <button
                  onClick={() => setPassSubTab('expired')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    passSubTab === 'expired'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  data-testid="pass-sub-expired"
                >
                  만료 이용권
                  {expiredPasses.length > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
                      passSubTab === 'expired' ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>{expiredPasses.length}</span>
                  )}
                </button>
              </div>
              <Button size="sm" className="bg-gray-900 hover:bg-gray-800" onClick={() => setPassDialogOpen(true)}>
                <Plus className="mr-1 h-4 w-4" />
                이용권 구매하기
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {passSubTab === 'active' ? (
                activePasses.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">보유 중인 이용권이 없습니다.</p>
                ) : (
                  activePasses.map((pass) => (
                    <div key={pass._id} className="border-b pb-4 last:border-b-0">
                      <div className="flex justify-between items-start mb-1">
                        <div>
                          <h3 className="font-semibold text-lg">{pass.name}</h3>
                          <p className="text-sm text-gray-500">
                            {pass.pass_type === 'period' ? '기간제' : '횟수제'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => openEditPass(pass)}>
                            수정
                          </Button>
                          {pass.pass_type === 'period' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-orange-600 border-orange-300 hover:bg-orange-50"
                              onClick={() => openUpgradeDialog(pass)}
                              data-testid={`upgrade-pass-btn-${pass._id}`}
                            >
                              업그레이드
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => openRefundDialog(pass)}
                            data-testid={`refund-pass-btn-${pass._id}`}
                          >
                            환불
                          </Button>
                          <span className="px-3 py-1 rounded text-sm bg-green-100 text-green-700 font-medium">
                            활성
                          </span>
                        </div>
                      </div>
                      <div className="text-sm space-y-1 mt-2">
                        <p>기간: {formatDate(pass.start_date)} ~ {formatDate(pass.end_date)}</p>
                        {isCurrentlyHolding(pass) && (
                          <div>
                            <p className="text-purple-600 font-medium">
                              홀딩: {formatDate(pass.holding_start)} ~ {formatDate(pass.holding_end)} ({getRemainingHoldingDays(pass.holding_end)}일 남음)
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-2 text-purple-700 border-purple-300 hover:bg-purple-50"
                              onClick={() => { setResumePassId(pass._id); setResumeDialogOpen(true); }}
                            >
                              오늘부터 시작하기
                            </Button>
                          </div>
                        )}
                        {pass.pass_type === 'count' && (
                          <p>잔여 횟수: {pass.remaining_count} / {pass.total_count}</p>
                        )}
                        {Array.isArray(pass.upgrade_history) && pass.upgrade_history.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-orange-100" data-testid={`upgrade-history-${pass._id}`}>
                            <p className="text-xs font-medium text-orange-700 mb-2">업그레이드 이력</p>
                            <ul className="space-y-1.5">
                              {pass.upgrade_history.map((h, idx) => (
                                <li
                                  key={idx}
                                  className="text-xs text-gray-700 bg-orange-50 border border-orange-100 rounded px-2.5 py-1.5"
                                  data-testid={`upgrade-history-item-${pass._id}-${idx}`}
                                >
                                  <span className="text-gray-500">{formatDate(h.upgraded_at)}</span>
                                  {' · '}
                                  <span className="font-medium">{h.from_name}</span>
                                  <span className="text-gray-400 mx-1">({h.from_duration_days}일)</span>
                                  <span className="mx-1">→</span>
                                  <span className="font-medium text-orange-700">{h.to_name}</span>
                                  <span className="text-gray-400 mx-1">({h.to_duration_days}일)</span>
                                  {h.diff_price > 0 && (
                                    <span className="ml-1 text-gray-600">· 차액 {Number(h.diff_price).toLocaleString()}원</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )
              ) : (
                expiredPasses.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">만료된 이용권이 없습니다.</p>
                ) : (
                  expiredPasses.map((pass) => (
                    <div key={pass._id} className="border-b pb-3 last:border-b-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium">{pass.name}</h3>
                          <p className="text-sm text-gray-500">
                            {pass.pass_type === 'period' ? '기간제' : '횟수제'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatDate(pass.start_date)} ~ {formatDate(pass.end_date)}
                          </p>
                          {pass.pass_type === 'count' && (
                            <p className="text-sm text-gray-500">잔여 횟수: {pass.remaining_count} / {pass.total_count}</p>
                          )}
                          {pass.status === 'refunded' && (
                            <p className="text-sm text-red-600 mt-1">
                              환불 {pass.refund_amount ? `${pass.refund_amount.toLocaleString()}원` : ''}
                              {pass.refund_reason ? ` · ${pass.refund_reason}` : ''}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => openEditPass(pass)}>
                            수정
                          </Button>
                          {pass.status === 'refunded' ? (
                            <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-700 font-medium">
                              환불됨
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-600">
                              만료
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )
              )}
            </CardContent>
          </Card>

          {/* Holding History */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base">홀딩 내역</CardTitle>
            </CardHeader>
            <CardContent>
              {holdingHistory.length === 0 ? (
                <p className="text-gray-500 text-center py-4">홀딩 내역 없음</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-3 text-left">이용권명</th>
                        <th className="p-3 text-left">홀딩 시작일</th>
                        <th className="p-3 text-left">홀딩 종료일</th>
                        <th className="p-3 text-left">홀딩 일수</th>
                        <th className="p-3 text-left">연장 일수</th>
                        <th className="p-3 text-left">원래 종료일 → 변경된 종료일</th>
                        <th className="p-3 text-left">상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holdingHistory.map((h, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="p-3">{h.pass_name}</td>
                          <td className="p-3">{formatDate(h.holding_start)}</td>
                          <td className="p-3">{formatDate(h.holding_end)}</td>
                          <td className="p-3">{h.holding_days}일</td>
                          <td className="p-3 font-medium text-blue-700">+{h.extension_days}일</td>
                          <td className="p-3 whitespace-nowrap">
                            {h.original_end_date ? (
                              <span className="text-gray-700">
                                {formatDate(h.original_end_date)}
                                <span className="mx-1.5 text-gray-400">→</span>
                                <span className="font-medium">{formatDate(h.extended_end_date)}</span>
                              </span>
                            ) : (
                              <span className="text-gray-500">{formatDate(h.extended_end_date)}</span>
                            )}
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              h.status === '진행중' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {h.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== 예약 내역 탭 ========== */}
        <TabsContent value="reservations" className="space-y-4">
          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Calendar */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold">
                      {calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h3>
                    <div className="flex gap-2">
                      <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded">
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center text-sm">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                      <div key={day} className="p-2 font-medium text-gray-600">{day}</div>
                    ))}
                    {getCalendarDays.map((d, idx) => (
                      <div 
                        key={idx} 
                        className={`p-2 ${d.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`}
                      >
                        {d.day}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center">
                    <Calendar className="mr-2 h-5 w-5" />
                    이달 예약 내역
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b">
                      <span>총 예약</span>
                      <span className="font-medium">{reservationStats.total}건</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span>출석</span>
                      <span className="font-medium">{reservationStats.attended}건</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span>노쇼처리</span>
                      <span className="font-medium">{reservationStats.noShow}건</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reservation Table */}
              <div className="mt-8">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="p-3 text-left font-medium">수업 명 ↕</th>
                        <th className="p-3 text-left font-medium">예약 날짜 ↕</th>
                        <th className="p-3 text-left font-medium">수업 시간</th>
                        <th className="p-3 text-left font-medium">상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservations.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="p-4 text-center text-gray-500">
                            예약 내역이 없습니다.
                          </td>
                        </tr>
                      ) : (
                        reservations.slice(0, 10).map((res) => {
                          const scheduledAt = res.session?.scheduled_at ? new Date(res.session.scheduled_at) : null;
                          return (
                            <tr key={res._id} className="border-b hover:bg-gray-50">
                              <td className="p-3">{res.session?.snapshot_name || '-'}</td>
                              <td className="p-3">
                                {scheduledAt ? formatDate(scheduledAt) : '-'}
                              </td>
                              <td className="p-3">
                                {scheduledAt ? `${scheduledAt.getHours().toString().padStart(2, '0')}:${scheduledAt.getMinutes().toString().padStart(2, '0')} - ${(scheduledAt.getHours() + 1).toString().padStart(2, '0')}:${scheduledAt.getMinutes().toString().padStart(2, '0')}` : '-'}
                              </td>
                              <td className="p-3">
                                <span className={`font-medium ${
                                  res.status === 'confirmed' || res.attendance_status === 'present' ? 'text-green-600' :
                                  res.status === 'no_show' ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                  {res.attendance_status === 'present' ? '출석' :
                                   res.status === 'no_show' ? '노쇼' :
                                   res.status === 'cancelled' ? '취소' :
                                   res.status === 'confirmed' ? '예약' : res.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== 측정 데이터 탭 ========== */}
        <TabsContent value="measurements" className="space-y-4">
          <Card>
            <CardHeader className="py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center text-base">
                  <Activity className="mr-2 h-5 w-5" />
                  측정 데이터 이력
                </CardTitle>
                {/* 카테고리 필터 */}
                <div className="flex gap-2">
                  {[
                    { key: 'all', label: '전체' },
                    { key: 'strength', label: '근력', color: 'bg-red-100 text-red-700 hover:bg-red-200' },
                    { key: 'muscular_endurance', label: '근지구력', color: 'bg-orange-100 text-orange-700 hover:bg-orange-200' },
                    { key: 'cardio', label: '심폐지구력', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
                  ].map(filter => (
                    <button
                      key={filter.key}
                      onClick={() => setMeasurementFilter(filter.key)}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        measurementFilter === filter.key
                          ? filter.key === 'all' 
                            ? 'bg-gray-900 text-white' 
                            : filter.color + ' ring-2 ring-offset-1 ring-gray-400'
                          : filter.key === 'all'
                            ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            : filter.color + ' opacity-60'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {measurements.length === 0 ? (
                <p className="text-gray-500 text-center py-8">측정 데이터가 없습니다.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-3 text-left">측정일</th>
                        <th className="p-3 text-left">카테고리</th>
                        <th className="p-3 text-left">운동</th>
                        <th className="p-3 text-left">측정값</th>
                        <th className="p-3 text-left">계산값</th>
                        <th className="p-3 text-left">점수</th>
                        <th className="p-3 text-center w-16">관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...measurements]
                        .filter(m => measurementFilter === 'all' || m.category === measurementFilter)
                        .sort((a, b) => new Date(b.measured_at) - new Date(a.measured_at))
                        .slice(0, 50)
                        .map((m) => {
                          // 카테고리별 색상 배지 설정
                          const categoryConfig = {
                            strength: { name: '근력', bgColor: 'bg-red-100', textColor: 'text-red-700' },
                            muscular_endurance: { name: '근지구력', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
                            cardio: { name: '심폐지구력', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
                            inbody: { name: '체성분', bgColor: 'bg-purple-100', textColor: 'text-purple-700' },
                            posture: { name: '자세', bgColor: 'bg-green-100', textColor: 'text-green-700' }
                          };
                          const catConfig = categoryConfig[m.category] || { name: m.category, bgColor: 'bg-gray-100', textColor: 'text-gray-700' };

                          // 종목명 한글 매핑
                          const exerciseLabels = {
                            deadlift: '데드리프트',
                            squat: '스쿼트',
                            bench_press: '벤치프레스',
                            shoulder_press: '숄더프레스',
                            pushup: '푸시업',
                            situp: '싯업',
                            barbell_squat_endurance: '바벨 스쿼트',
                            barbell_deadlift_endurance: '바벨 데드리프트',
                            barbell_shoulder_press_endurance: '바벨 숄더프레스',
                            plank: '플랭크',
                            wall_squat: '월 스쿼트',
                            amrap1: 'AMRAP1',
                            amrap2: 'AMRAP2',
                            amrap3: 'AMRAP3',
                            amrap4: 'AMRAP4',
                            running: '러닝',
                            rowing: '로잉',
                            inbody: '인바디',
                            posture: '자세평가',
                          };
                          const exerciseName = exerciseLabels[m.exercise] || m.exercise || '-';

                          // Format raw metrics - 우선순위 순서
                          let metricsDisplay = '-';
                          if (m.raw_metrics) {
                            const rm = m.raw_metrics;
                            if (rm.weight_kg && rm.reps) {
                              metricsDisplay = `${rm.weight_kg}kg × ${rm.reps}회`;
                            } else if (rm.distance_m && rm.duration_seconds) {
                              metricsDisplay = `${rm.distance_m}m / ${rm.duration_seconds}초`;
                            } else if (rm.distance_m) {
                              metricsDisplay = `${rm.distance_m}m`;
                            } else if (rm.reps && rm.input_load_kg) {
                              metricsDisplay = `${rm.reps}회 × ${rm.input_load_kg}kg`;
                            } else if (rm.reps) {
                              metricsDisplay = `${rm.reps}회`;
                            } else if (rm.seconds) {
                              metricsDisplay = `${rm.seconds}초`;
                            } else if (rm.body_fat_pct !== undefined || rm.skeletal_muscle_kg !== undefined) {
                              // 인바디
                              const parts = [];
                              if (rm.weight_kg) parts.push(`${rm.weight_kg}kg`);
                              if (rm.body_fat_pct !== undefined) parts.push(`체지방 ${rm.body_fat_pct}%`);
                              if (rm.skeletal_muscle_kg !== undefined) parts.push(`골격근 ${rm.skeletal_muscle_kg}kg`);
                              metricsDisplay = parts.join(' · ') || '-';
                            } else if (rm.posture_score !== undefined) {
                              metricsDisplay = `${rm.posture_score}점`;
                            }
                          }

                          // Format computed metrics
                          let computedDisplay = '-';
                          if (m.computed_metrics) {
                            const cm = m.computed_metrics;
                            if (cm.one_rm) {
                              computedDisplay = `1RM: ${cm.one_rm?.toFixed(1) || '-'}kg`;
                              if (cm.relative_strength) {
                                computedDisplay += ` · 상대근력 ${cm.relative_strength.toFixed(2)}`;
                              }
                            } else if (cm.vo2) {
                              computedDisplay = `VO2max: ${cm.vo2?.toFixed(1) || '-'}`;
                            } else if (cm.base_load_kg !== undefined) {
                              // 바벨 근지구력 load 보정
                              computedDisplay = `기준중량 ${cm.base_load_kg}kg (${cm.load_diff_pct > 0 ? '+' : ''}${cm.load_diff_pct}%)`;
                            }
                          }
                          
                          return (
                            <tr key={m._id} className="border-b hover:bg-gray-50">
                              <td className="p-3">{formatDate(m.measured_at)}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${catConfig.bgColor} ${catConfig.textColor}`}>
                                  {catConfig.name}
                                </span>
                              </td>
                              <td className="p-3">{exerciseName}</td>
                              <td className="p-3">{metricsDisplay}</td>
                              <td className="p-3">{computedDisplay}</td>
                              <td className="p-3">
                                {m.score ? (
                                  <span className="font-medium text-blue-600">
                                    {m.score.toFixed(1)}점
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="p-3 text-center">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setDeleteDialog({ open: true, measurement: m })}
                                  className="h-8 w-8 p-0 hover:bg-red-50"
                                  data-testid={`delete-measurement-${m._id}`}
                                  title="삭제"
                                >
                                  <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                  {/* 필터링 결과 없음 표시 */}
                  {measurements.filter(m => measurementFilter === 'all' || m.category === measurementFilter).length === 0 && (
                    <p className="text-gray-500 text-center py-8">해당 카테고리의 측정 데이터가 없습니다.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== 피트니스 점수 탭 ========== */}
        <TabsContent value="score" className="space-y-6">
          {/* 상단: 레이더 차트 + 점수 요약 */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-center">오각형 (종합 밸런스)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Radar Chart with Legend */}
                <div className="flex flex-col items-center relative">
                  {/* Toggle Button - Top Right */}
                  {hasAverageData && (
                    <button
                      onClick={() => setShowAverageOnChart(!showAverageOnChart)}
                      className={`absolute top-0 right-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        showAverageOnChart 
                          ? 'bg-orange-100 text-orange-600 border border-orange-300 hover:bg-orange-200' 
                          : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {showAverageOnChart ? '연령 평균 숨기기' : '연령 평균 표시하기'}
                    </button>
                  )}
                  
                  <div className="flex gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: '#c9a65f' }}></div>
                      <span className="text-sm">회원님</span>
                    </div>
                    {showAverageOnChart && hasAverageData && (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-orange-400"></div>
                        <span className="text-sm">연령 평균</span>
                      </div>
                    )}
                  </div>
                  {radarData.length > 0 ? (
                    <ResponsiveContainer width={400} height={350}>
                      <RadarChart data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                        <Radar
                          name="회원님"
                          dataKey="score"
                          stroke="#c9a65f"
                          fill="#c9a65f"
                          fillOpacity={0.5}
                        />
                        {showAverageOnChart && hasAverageData && (
                          <Radar
                            name="연령 평균"
                            dataKey="average"
                            stroke="#fb923c"
                            fill="#fb923c"
                            fillOpacity={0.3}
                          />
                        )}
                      </RadarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 py-20">측정된 점수가 없습니다.</p>
                  )}
                </div>

                {/* Score Summary with Grades */}
                <div className="border rounded-lg p-6">
                  <h3 className="font-semibold mb-2">점수 요약</h3>
                  <p className="text-sm text-gray-500 mb-4">회원의 최근 데이터 기반 점수</p>
                  
                  {fitness_snapshot ? (
                    <div className="space-y-3">
                      {[
                        { label: '근 력', score: fitness_snapshot.strength_score || 0 },
                        { label: '근지구력', score: fitness_snapshot.muscular_endurance_score || 0 },
                        { label: '심폐지구력', score: fitness_snapshot.cardio_score || 0 },
                        { label: '신체 조성', score: fitness_snapshot.body_composition_score || 0 },
                        { label: '신체 균형', score: fitness_snapshot.balance_score || 0 },
                      ].map((item, idx) => {
                        const grade = getScoreGrade(item.score);
                        return (
                          <div key={idx} className="flex justify-between items-center">
                            <span>{item.label}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{item.score.toFixed(0)} 점</span>
                              <span className={`text-sm ${grade.color}`}>/ {grade.label}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500">점수 데이터 없음</p>
                  )}
                  
                  <p className="text-xs text-gray-400 mt-6">
                    종합 밸런스 점수는 다양한 기록 요소와 데이터를 포함하며, 최적의 추정 점수를 자동으로 계산합니다.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 하단: 고객 데이터 (탭 + 그래프 + 상세 카드) */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-4">
              <div className="flex items-center gap-4">
                <CardTitle className="text-base">고객 데이터</CardTitle>
                {/* 탭: 운동 기록 / 측정 기록 */}
                <div className="flex gap-1">
                  <button
                    onClick={() => { setScoreDataTab('exercise'); setSelectedChartPoint(null); }}
                    className={`px-3 py-1 text-sm rounded ${
                      scoreDataTab === 'exercise' 
                        ? 'bg-amber-100 text-amber-800 font-medium' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    운동 기록
                  </button>
                  <button
                    onClick={() => { setScoreDataTab('measurement'); setSelectedChartPoint(null); }}
                    className={`px-3 py-1 text-sm rounded ${
                      scoreDataTab === 'measurement' 
                        ? 'bg-amber-100 text-amber-800 font-medium' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    측정 기록
                  </button>
                </div>
              </div>
              {/* 기간 필터 */}
              <div className="flex gap-2">
                {[
                  { value: '4', label: '4주' },
                  { value: '12', label: '12주' },
                  { value: '24', label: '24주' },
                  { value: '52', label: '1년' },
                  { value: 'all', label: '전체' },
                ].map(p => (
                  <button
                    key={p.value}
                    onClick={() => setScorePeriod(p.value)}
                    className={`px-3 py-1 text-sm rounded ${
                      scorePeriod === p.value ? 'bg-gray-900 text-white' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* 그래프 영역 */}
                <div className="space-y-6">
                  {scoreDataTab === 'exercise' ? (
                    <>
                      {/* 근력 차트 */}
                      <div>
                        <h4 className="font-medium mb-2 text-center">근력</h4>
                        <ResponsiveContainer width="100%" height={130}>
                          <LineChart data={scoreHistoryData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Line 
                              type="monotone" 
                              dataKey="strength" 
                              stroke="#333" 
                              dot={{ fill: '#333', r: 4, cursor: 'pointer' }}
                              activeDot={{ 
                                r: 8, 
                                fill: '#c9a65f',
                                stroke: '#333',
                                strokeWidth: 2,
                                onClick: (e, payload) => {
                                  if (payload?.payload?.strengthData) {
                                    setSelectedChartPoint({ category: 'strength', data: payload.payload.strengthData });
                                  }
                                }
                              }}
                              connectNulls 
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>

                      {/* 근지구력 차트 */}
                      <div>
                        <h4 className="font-medium mb-2 text-center">근지구력</h4>
                        <ResponsiveContainer width="100%" height={130}>
                          <LineChart data={scoreHistoryData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Line 
                              type="monotone" 
                              dataKey="endurance" 
                              stroke="#333" 
                              dot={{ fill: '#333', r: 4, cursor: 'pointer' }}
                              activeDot={{ 
                                r: 8, 
                                fill: '#c9a65f',
                                stroke: '#333',
                                strokeWidth: 2,
                                onClick: (e, payload) => {
                                  if (payload?.payload?.enduranceData) {
                                    setSelectedChartPoint({ category: 'endurance', data: payload.payload.enduranceData });
                                  }
                                }
                              }}
                              connectNulls 
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>

                      {/* 심폐지구력 차트 */}
                      <div>
                        <h4 className="font-medium mb-2 text-center">심폐지구력</h4>
                        <ResponsiveContainer width="100%" height={130}>
                          <LineChart data={scoreHistoryData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Line 
                              type="monotone" 
                              dataKey="cardio" 
                              stroke="#333" 
                              dot={{ fill: '#333', r: 4, cursor: 'pointer' }}
                              activeDot={{ 
                                r: 8, 
                                fill: '#c9a65f',
                                stroke: '#333',
                                strokeWidth: 2,
                                onClick: (e, payload) => {
                                  if (payload?.payload?.cardioData) {
                                    setSelectedChartPoint({ category: 'cardio', data: payload.payload.cardioData });
                                  }
                                }
                              }}
                              connectNulls 
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* 신체 조성 차트 */}
                      <div>
                        <h4 className="font-medium mb-2 text-center">신체 조성</h4>
                        <ResponsiveContainer width="100%" height={150}>
                          <LineChart data={measurementHistoryData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Line 
                              type="monotone" 
                              dataKey="bodyComposition" 
                              stroke="#333" 
                              dot={{ fill: '#333', r: 4, cursor: 'pointer' }}
                              activeDot={{ 
                                r: 8, 
                                fill: '#c9a65f',
                                stroke: '#333',
                                strokeWidth: 2,
                                onClick: (e, payload) => {
                                  if (payload?.payload?.bodyCompositionData) {
                                    setSelectedChartPoint({ category: 'bodyComposition', data: payload.payload.bodyCompositionData });
                                  }
                                }
                              }}
                              connectNulls 
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>

                      {/* 신체 균형 차트 */}
                      <div>
                        <h4 className="font-medium mb-2 text-center">신체 균형</h4>
                        <ResponsiveContainer width="100%" height={150}>
                          <LineChart data={measurementHistoryData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Line 
                              type="monotone" 
                              dataKey="balance" 
                              stroke="#333" 
                              dot={{ fill: '#333', r: 4, cursor: 'pointer' }}
                              activeDot={{ 
                                r: 8, 
                                fill: '#c9a65f',
                                stroke: '#333',
                                strokeWidth: 2,
                                onClick: (e, payload) => {
                                  if (payload?.payload?.balanceData) {
                                    setSelectedChartPoint({ category: 'balance', data: payload.payload.balanceData });
                                  }
                                }
                              }}
                              connectNulls 
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </>
                  )}
                </div>

                {/* 상세 카드 영역 */}
                <div className="space-y-4">
                  {scoreDataTab === 'exercise' ? (
                    <>
                      {/* 선택된 데이터 또는 최신 데이터 표시 */}
                      {(() => {
                        const strengthData = selectedChartPoint?.category === 'strength' 
                          ? selectedChartPoint.data 
                          : latestStrength;
                        const enduranceData = selectedChartPoint?.category === 'endurance' 
                          ? selectedChartPoint.data 
                          : latestEndurance;
                        const cardioData = selectedChartPoint?.category === 'cardio' 
                          ? selectedChartPoint.data 
                          : latestCardio;

                        return (
                          <>
                            {/* 근력 세부 정보 */}
                            {strengthData && (
                              <div className={`border rounded-lg p-4 ${selectedChartPoint?.category === 'strength' ? 'border-amber-400 bg-amber-50' : ''}`}>
                                <h4 className="font-medium mb-2">측정 세부 정보</h4>
                                <div className="text-sm space-y-1">
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">운동 동작</span>
                                    <span>{getExerciseLabel(strengthData.exercise)}</span>
                                  </div>
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">날짜</span>
                                    <span>{formatDate(strengthData.measured_at)}</span>
                                  </div>
                                  {buildMetricRows(strengthData).map(([label, value], i) => (
                                    <div key={i} className="flex">
                                      <span className="w-24 text-gray-500 shrink-0">{label}</span>
                                      <span>{value}</span>
                                    </div>
                                  ))}
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">점수</span>
                                    <span className="font-medium text-blue-600">{strengthData.score?.toFixed(0) || '-'} 점</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* 근지구력 세부 정보 */}
                            {enduranceData && (
                              <div className={`border rounded-lg p-4 ${selectedChartPoint?.category === 'endurance' ? 'border-amber-400 bg-amber-50' : ''}`}>
                                <h4 className="font-medium mb-2">측정 세부 정보</h4>
                                <div className="text-sm space-y-1">
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">운동 동작</span>
                                    <span>{getExerciseLabel(enduranceData.exercise)}</span>
                                  </div>
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">날짜</span>
                                    <span>{formatDate(enduranceData.measured_at)}</span>
                                  </div>
                                  {buildMetricRows(enduranceData).map(([label, value], i) => (
                                    <div key={i} className="flex">
                                      <span className="w-24 text-gray-500 shrink-0">{label}</span>
                                      <span>{value}</span>
                                    </div>
                                  ))}
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">점수</span>
                                    <span className="font-medium text-blue-600">{enduranceData.score?.toFixed(0) || '-'} 점</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* 심폐지구력 세부 정보 */}
                            {cardioData && (
                              <div className={`border rounded-lg p-4 ${selectedChartPoint?.category === 'cardio' ? 'border-amber-400 bg-amber-50' : ''}`}>
                                <h4 className="font-medium mb-2">측정 세부 정보</h4>
                                <div className="text-sm space-y-1">
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">운동 동작</span>
                                    <span>{getExerciseLabel(cardioData.exercise)}</span>
                                  </div>
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">날짜</span>
                                    <span>{formatDate(cardioData.measured_at)}</span>
                                  </div>
                                  {buildMetricRows(cardioData).map(([label, value], i) => (
                                    <div key={i} className="flex">
                                      <span className="w-24 text-gray-500 shrink-0">{label}</span>
                                      <span>{value}</span>
                                    </div>
                                  ))}
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">점수</span>
                                    <span className="font-medium text-blue-600">{cardioData.score?.toFixed(0) || '-'} 점</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {!strengthData && !enduranceData && !cardioData && (
                              <p className="text-gray-500 text-center py-8">운동 기록 데이터가 없습니다.</p>
                            )}
                          </>
                        );
                      })()}
                    </>
                  ) : (
                    <>
                      {/* 측정 기록 탭 상세 카드 */}
                      {(() => {
                        const bodyCompData = selectedChartPoint?.category === 'bodyComposition' 
                          ? selectedChartPoint.data 
                          : measurements.filter(m => m.category === 'inbody').sort((a, b) => new Date(b.measured_at) - new Date(a.measured_at))[0];
                        const balanceData = selectedChartPoint?.category === 'balance' 
                          ? selectedChartPoint.data 
                          : measurements.filter(m => m.category === 'posture').sort((a, b) => new Date(b.measured_at) - new Date(a.measured_at))[0];

                        return (
                          <>
                            {/* 신체 조성 세부 정보 */}
                            {bodyCompData && (
                              <div className={`border rounded-lg p-4 ${selectedChartPoint?.category === 'bodyComposition' ? 'border-amber-400 bg-amber-50' : ''}`}>
                                <h4 className="font-medium mb-2">측정 세부 정보</h4>
                                <div className="text-sm space-y-1">
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">측정 항목</span>
                                    <span>{getExerciseLabel(bodyCompData.exercise) || 'InBody'}</span>
                                  </div>
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">날짜</span>
                                    <span>{formatDate(bodyCompData.measured_at)}</span>
                                  </div>
                                  {buildMetricRows(bodyCompData).map(([label, value], i) => (
                                    <div key={i} className="flex">
                                      <span className="w-24 text-gray-500 shrink-0">{label}</span>
                                      <span>{value}</span>
                                    </div>
                                  ))}
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">점수</span>
                                    <span className="font-medium text-blue-600">{bodyCompData.score?.toFixed(0) || '-'} 점</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* 신체 균형 세부 정보 */}
                            {balanceData && (
                              <div className={`border rounded-lg p-4 ${selectedChartPoint?.category === 'balance' ? 'border-amber-400 bg-amber-50' : ''}`}>
                                <h4 className="font-medium mb-2">측정 세부 정보</h4>
                                <div className="text-sm space-y-1">
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">측정 항목</span>
                                    <span>{getExerciseLabel(balanceData.exercise) || '자세 평가'}</span>
                                  </div>
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">날짜</span>
                                    <span>{formatDate(balanceData.measured_at)}</span>
                                  </div>
                                  {buildMetricRows(balanceData).map(([label, value], i) => (
                                    <div key={i} className="flex">
                                      <span className="w-24 text-gray-500 shrink-0">{label}</span>
                                      <span>{value}</span>
                                    </div>
                                  ))}
                                  <div className="flex">
                                    <span className="w-24 text-gray-500 shrink-0">점수</span>
                                    <span className="font-medium text-blue-600">{balanceData.score?.toFixed(0) || '-'} 점</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {!bodyCompData && !balanceData && (
                              <p className="text-gray-500 text-center py-8">측정 기록 데이터가 없습니다.</p>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ========== Insight History (read-only) ========== */}
          <MemberInsightHistory memberId={member._id} />
        </TabsContent>

        {/* ========== 결제내역 탭 ========== */}
        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="flex items-center text-base">
                <DollarSign className="mr-2 h-5 w-5" />
                결제 내역
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!transactions || transactions.length === 0) ? (
                <p className="text-gray-500 text-center py-8">결제 내역이 없습니다.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-3 text-left">날짜</th>
                        <th className="p-3 text-left">내역</th>
                        <th className="p-3 text-left">유형</th>
                        <th className="p-3 text-left">변동</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((t, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="p-3">{formatDate(t.created_at)}</td>
                          <td className="p-3">{t.description || '-'}</td>
                          <td className="p-3">{t.type}</td>
                          <td className="p-3">
                            <span className={t.amount > 0 ? 'text-green-600' : 'text-red-600'}>
                              {t.amount > 0 ? '+' : ''}{t.amount}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== 출석 내역 탭 ========== */}
        <TabsContent value="attendance" className="space-y-4">
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="flex items-center text-base">
                <CheckCircle className="mr-2 h-5 w-5" />
                출석 내역
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!attendances || attendances.length === 0) ? (
                <p className="text-gray-500 text-center py-8">출석 내역이 없습니다.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-3 text-left">날짜</th>
                        <th className="p-3 text-left">수업명</th>
                        <th className="p-3 text-left">체크인 시간</th>
                        <th className="p-3 text-left">상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendances.map((a, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="p-3">{formatDate(a.checked_in_at)}</td>
                          <td className="p-3">{a.session_name || '-'}</td>
                          <td className="p-3">
                            {a.checked_in_at ? new Date(a.checked_in_at).toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              a.status === 'present' ? 'bg-green-100 text-green-700' :
                              a.status === 'late' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {a.status === 'present' ? '출석' :
                               a.status === 'late' ? '지각' : a.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== 메모 탭 ========== */}
        <TabsContent value="memo" className="space-y-4">
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="flex items-center text-base">
                <FileText className="mr-2 h-5 w-5" />
                회원 메모
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={memoText}
                onChange={(e) => setMemoText(e.target.value)}
                placeholder="회원에 대한 메모를 입력하세요..."
                rows={6}
                className="mb-4"
              />
              <Button 
                onClick={handleSaveMemo}
                disabled={memoLoading}
                className="bg-gray-900 hover:bg-gray-800"
              >
                {memoLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    저장 중...
                  </>
                ) : (
                  '메모 저장'
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Member Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>회원 정보 수정</DialogTitle>
            <DialogDescription>
              회원 정보를 수정합니다.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">이름</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-phone">전화번호</Label>
              <Input
                id="edit-phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-email">이메일</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-gender">성별</Label>
                <select
                  id="edit-gender"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={editForm.gender}
                  onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                >
                  <option value="">선택</option>
                  <option value="male">남성</option>
                  <option value="female">여성</option>
                </select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-birth">생년월일</Label>
                <Input
                  id="edit-birth"
                  type="date"
                  value={editForm.birth_date}
                  onChange={(e) => setEditForm({ ...editForm, birth_date: e.target.value })}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-weight">체중 (kg)</Label>
              <Input
                id="edit-weight"
                type="number"
                step="0.1"
                min="30"
                max="200"
                placeholder="예: 70.5"
                value={editForm.body_weight_kg}
                onChange={(e) => setEditForm({ ...editForm, body_weight_kg: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-actual-start">실제 운동 시작일</Label>
              <Input
                id="edit-actual-start"
                type="date"
                value={editForm.actual_start_date}
                onChange={(e) => setEditForm({ ...editForm, actual_start_date: e.target.value })}
                data-testid="edit-actual-start-input"
              />
              <p className="text-xs text-gray-500">앱 등록일이 아닌 실제 센터 운동 시작일. 입력 시 회원 앱 가입일에 우선 표시됩니다.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              취소
            </Button>
            <Button onClick={handleEditMember} disabled={editLoading}>
              {editLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  저장 중...
                </>
              ) : (
                '저장'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pass Registration Dialog */}
      <Dialog open={passDialogOpen} onOpenChange={setPassDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>이용권 등록</DialogTitle>
            <DialogDescription>
              회원에게 새로운 이용권을 등록합니다.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* 상품 선택 */}
            <div className="grid gap-2">
              <Label>상품 선택</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedProductId}
                onChange={(e) => handleProductSelect(e.target.value)}
              >
                <option value="">직접 입력</option>
                {products.map(p => (
                  <option key={p._id} value={p._id}>
                    {p.name} ({p.duration_days}일{p.pass_type === 'count' ? ` / ${p.total_count}회` : ''})
                  </option>
                ))}
              </select>
              {products.length === 0 && (
                <p className="text-xs text-gray-500">
                  등록된 상품이 없습니다. <a href="/admin/products" className="text-blue-600 underline">상품관리</a>에서 추가하세요.
                </p>
              )}
            </div>

            {/* 시작일 */}
            <div className="grid gap-2">
              <Label htmlFor="pass-start">시작일 *</Label>
              <Input
                id="pass-start"
                type="date"
                value={passForm.start_date}
                onChange={(e) => handleStartDateChange(e.target.value)}
              />
            </div>

            {/* 이용권 유형 (상품 미선택 시에만 수정 가능) */}
            {!selectedProductId && (
              <div className="grid gap-2">
                <Label>이용권 유형</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="pass_type"
                      value="period"
                      checked={passForm.pass_type === 'period'}
                      onChange={(e) => setPassForm({ ...passForm, pass_type: e.target.value })}
                      className="w-4 h-4"
                    />
                    <span>기간제</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="pass_type"
                      value="count"
                      checked={passForm.pass_type === 'count'}
                      onChange={(e) => setPassForm({ ...passForm, pass_type: e.target.value })}
                      className="w-4 h-4"
                    />
                    <span>횟수제</span>
                  </label>
                </div>
              </div>
            )}

            {/* 이용권 이름 */}
            <div className="grid gap-2">
              <Label htmlFor="pass-name">이용권 이름 *</Label>
              <Input
                id="pass-name"
                placeholder={passForm.pass_type === 'period' ? '예: 1개월 무제한' : '예: 10회 이용권'}
                value={passForm.name}
                onChange={(e) => setPassForm({ ...passForm, name: e.target.value })}
                disabled={!!selectedProductId}
              />
            </div>

            {/* 종료일 */}
            <div className="grid gap-2">
              <Label htmlFor="pass-end">종료일 *</Label>
              <Input
                id="pass-end"
                type="date"
                value={passForm.end_date}
                onChange={(e) => {
                  setPassForm({ ...passForm, end_date: e.target.value });
                  setEndDateManuallyEdited(true);
                }}
              />
              {selectedProductId && !endDateManuallyEdited && (
                <p className="text-xs text-gray-500">시작일 기준 자동 계산 (직접 수정 가능)</p>
              )}
            </div>

            {/* 횟수 (횟수제일 경우) */}
            {passForm.pass_type === 'count' && (
              <div className="grid gap-2">
                <Label htmlFor="pass-count">총 횟수 *</Label>
                <Input
                  id="pass-count"
                  type="number"
                  min="1"
                  value={passForm.total_count}
                  onChange={(e) => setPassForm({ ...passForm, total_count: e.target.value })}
                  disabled={!!selectedProductId}
                />
              </div>
            )}

            {/* 가격 */}
            <div className="grid gap-2">
              <Label htmlFor="pass-price">가격 (원)</Label>
              <Input
                id="pass-price"
                type="number"
                min="0"
                placeholder="예: 240000"
                value={passForm.price}
                onChange={(e) => setPassForm({ ...passForm, price: e.target.value })}
              />
              {selectedProductId && (
                <p className="text-xs text-gray-500">상품 기본 가격 (직접 수정 가능)</p>
              )}
            </div>

            {/* 미리보기 */}
            {passForm.start_date && passForm.end_date && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                <CreditCard className="inline h-4 w-4 mr-1" />
                이용 기간: {Math.ceil((new Date(passForm.end_date) - new Date(passForm.start_date)) / (1000 * 60 * 60 * 24)) + 1}일
                {passForm.pass_type === 'count' && ` / ${passForm.total_count}회`}
                {passForm.price && ` / ${Number(passForm.price).toLocaleString()}원`}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPassDialogOpen(false)}>
              취소
            </Button>
            <Button onClick={handleCreatePass} disabled={passLoading}>
              {passLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  등록 중...
                </>
              ) : (
                '등록'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 이용권 수정 모달 */}
      <Dialog open={editPassDialogOpen} onOpenChange={setEditPassDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>이용권 수정</DialogTitle>
            <DialogDescription>
              {editPassForm.name} ({editPassForm.pass_type === 'period' ? '기간제' : '횟수제'})
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>시작일</Label>
              <Input
                type="date"
                value={editPassForm.start_date}
                onChange={(e) => setEditPassForm({ ...editPassForm, start_date: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label>종료일</Label>
              <Input
                type="date"
                value={editPassForm.end_date}
                onChange={(e) => setEditPassForm({ ...editPassForm, end_date: e.target.value })}
              />
            </div>
            {editPassForm.pass_type === 'count' && (
              <>
                <div className="grid gap-2">
                  <Label>최초 횟수</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editPassForm.total_count}
                    onChange={(e) => setEditPassForm({ ...editPassForm, total_count: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>잔여 횟수</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editPassForm.remaining_count}
                    onChange={(e) => setEditPassForm({ ...editPassForm, remaining_count: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPassDialogOpen(false)}>취소</Button>
            <Button onClick={handleEditPassSave} disabled={editPassLoading}>
              {editPassLoading ? '저장 중...' : '저장'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 홀딩 조기 재개 확인 다이얼로그 */}
      <Dialog open={resumeDialogOpen} onOpenChange={(open) => !resumeLoading && setResumeDialogOpen(open)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>오늘부터 이용권을 다시 시작하시겠어요?</DialogTitle>
            <DialogDescription>
              남은 홀딩 기간은 종료되며, 실제 적용된 홀딩 일수 기준으로 이용권 종료일이 다시 계산됩니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResumeDialogOpen(false)} disabled={resumeLoading}>취소</Button>
            <Button onClick={handleResumePass} disabled={resumeLoading}>
              {resumeLoading ? '처리 중...' : '오늘부터 시작'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Pass Dialog */}
      <Dialog open={refundDialogOpen} onOpenChange={(open) => !refundLoading && setRefundDialogOpen(open)}>
        <DialogContent className="max-w-md" data-testid="refund-pass-dialog">
          <DialogHeader>
            <DialogTitle>이용권 환불</DialogTitle>
            <DialogDescription>
              이 이용권을 환불 처리합니다. 환불 내역은 매출 집계에서 자동으로 차감되며, 기록은 만료 이용권 탭에 보관됩니다.
            </DialogDescription>
          </DialogHeader>

          {refundPass && (
            <div className="py-2 space-y-4">
              <div className="rounded-lg border p-3 bg-gray-50 text-sm space-y-1">
                <div className="font-medium">{refundPass.name}</div>
                <div className="text-gray-600">
                  {formatDate(refundPass.start_date)} ~ {formatDate(refundPass.end_date)}
                </div>
                {refundPass.price ? (
                  <div className="text-gray-600">원 결제 금액: {Number(refundPass.price).toLocaleString()}원</div>
                ) : (
                  <div className="text-yellow-700 text-xs">※ 원 결제 금액 정보가 없어 환불 금액을 직접 입력해주세요</div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">환불 금액 (원)</label>
                <Input
                  type="number"
                  min={0}
                  max={refundPass.price || undefined}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  data-testid="refund-amount-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  전체 환불이 아니면 부분 환불 금액으로 입력할 수 있습니다.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">환불 사유 (선택)</label>
                <Input
                  type="text"
                  placeholder="예: 회원 요청, 건강상 이슈"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  data-testid="refund-reason-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundDialogOpen(false)} disabled={refundLoading}>
              취소
            </Button>
            <Button
              onClick={handleRefundPass}
              disabled={refundLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-refund-btn"
            >
              {refundLoading ? '처리 중...' : '환불 처리'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 회원 삭제 확인 다이얼로그 */}
      <Dialog open={deleteMemberDialogOpen} onOpenChange={(o) => !deleteMemberSubmitting && setDeleteMemberDialogOpen(o)}>
        <DialogContent className="max-w-md" data-testid="delete-member-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">회원을 삭제하시겠습니까?</DialogTitle>
            <DialogDescription>
              삭제 후에는 <b>복구할 수 없습니다</b>. 이 회원과 관련된 모든 데이터가 함께 영구 삭제됩니다.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-3 text-sm">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <div className="text-xs text-red-700 mb-1">삭제 대상</div>
              <div className="font-semibold text-base text-gray-900">{data?.member?.name}</div>
              <div className="text-gray-600">{data?.member?.phone}</div>
            </div>

            <div className="text-xs text-gray-700">
              아래 데이터가 <b>모두 삭제</b>됩니다:
              <ul className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 pl-1 text-gray-600">
                <li>• 회원 정보</li>
                <li>• 이용권</li>
                <li>• 예약 내역</li>
                <li>• 출석 내역</li>
                <li>• 측정 기록</li>
                <li>• 인바디 데이터</li>
                <li>• FIT REPORT</li>
                <li>• 결제 내역</li>
                <li>• 회원 메모</li>
                <li>• 기타 연결 데이터</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteMemberDialogOpen(false)}
              disabled={deleteMemberSubmitting}
              data-testid="delete-member-cancel"
            >
              취소
            </Button>
            <Button
              onClick={handleDeleteMember}
              disabled={deleteMemberSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-member-confirm"
            >
              {deleteMemberSubmitting ? '삭제 중...' : '회원 삭제'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 이용권 업그레이드 다이얼로그 */}
      <Dialog open={upgradeDialogOpen} onOpenChange={(o) => !upgradeSubmitting && setUpgradeDialogOpen(o)}>
        <DialogContent className="max-w-lg" data-testid="upgrade-pass-dialog">
          <DialogHeader>
            <DialogTitle>이용권 업그레이드</DialogTitle>
            <DialogDescription>
              기간제 이용권을 상위 상품으로 변경합니다. 시작일·홀딩·이용 기록은 그대로 유지되고, 종료일은 상품 기간 차이만큼 연장됩니다.
            </DialogDescription>
          </DialogHeader>

          {upgradeOptionsLoading ? (
            <div className="py-8 text-center text-sm text-gray-500" data-testid="upgrade-loading">
              <Loader2 className="inline h-4 w-4 mr-2 animate-spin" />
              업그레이드 옵션을 불러오는 중...
            </div>
          ) : upgradeCurrentMeta ? (
            <div className="py-2 space-y-4">
              {/* 현재 이용권 */}
              <div className="rounded-lg border p-3 bg-gray-50 text-sm space-y-1" data-testid="upgrade-current-info">
                <div className="text-xs text-gray-500">현재 상품</div>
                <div className="font-medium text-base">{upgradeCurrentMeta.name}</div>
                <div className="text-gray-600">
                  {formatDate(upgradeCurrentMeta.start_date)} ~ {formatDate(upgradeCurrentMeta.end_date)}
                  <span className="ml-2 text-gray-400">({upgradeCurrentMeta.duration_days}일)</span>
                </div>
              </div>

              {/* 변경할 상품 */}
              <div>
                <Label className="text-sm font-medium mb-1.5 block">변경할 상품</Label>
                {upgradeOptions.length === 0 ? (
                  <div className="text-sm text-gray-500 border rounded p-3 bg-amber-50 border-amber-200">
                    현재 상품보다 긴 기간의 활성 상품이 없습니다.
                  </div>
                ) : (
                  <select
                    value={upgradeSelectedProductId}
                    onChange={(e) => setUpgradeSelectedProductId(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="upgrade-product-select"
                  >
                    <option value="">상품을 선택해주세요</option>
                    {upgradeOptions.map((o) => (
                      <option key={o.product_id} value={o.product_id}>
                        {o.name} ({o.duration_days}일 · {Number(o.price).toLocaleString()}원)
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* 종료일 프리뷰 */}
              {upgradeNewEndDatePreview && (
                <div
                  className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm space-y-1"
                  data-testid="upgrade-preview"
                >
                  <div className="text-xs text-orange-700">변경 후 종료일</div>
                  <div className="font-medium text-orange-900">
                    {formatDate(upgradeCurrentMeta.end_date)} → {formatDate(upgradeNewEndDatePreview.iso)}
                  </div>
                  <div className="text-xs text-orange-700">
                    +{upgradeNewEndDatePreview.extension_days}일 연장 (홀딩 이력 그대로 유지)
                  </div>
                </div>
              )}

              {/* 차액 결제 */}
              <div>
                <Label className="text-sm font-medium mb-1.5 block">차액 결제금액 (원)</Label>
                <Input
                  type="number"
                  min={0}
                  value={upgradeDiffPrice}
                  onChange={(e) => setUpgradeDiffPrice(e.target.value)}
                  placeholder="예: 300000"
                  data-testid="upgrade-diff-input"
                />
                {selectedUpgradeTarget && upgradeCurrentMeta && (
                  <p className="text-xs text-gray-500 mt-1">
                    제안 차액: {Number(selectedUpgradeTarget.price - upgradeCurrentMeta.price).toLocaleString()}원
                    {' '}({Number(selectedUpgradeTarget.price).toLocaleString()} − {Number(upgradeCurrentMeta.price).toLocaleString()})
                  </p>
                )}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUpgradeDialogOpen(false)}
              disabled={upgradeSubmitting}
            >
              취소
            </Button>
            <Button
              onClick={handleSubmitUpgrade}
              disabled={upgradeSubmitting || !upgradeSelectedProductId || upgradeOptions.length === 0}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="upgrade-submit-btn"
            >
              {upgradeSubmitting ? '처리 중...' : '업그레이드 실행'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 측정 데이터 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, measurement: null })}>        <DialogContent>
          <DialogHeader>
            <DialogTitle>측정 데이터 삭제</DialogTitle>
            <DialogDescription>
              이 측정 데이터를 정말 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.
            </DialogDescription>
          </DialogHeader>
          {deleteDialog.measurement && (
            <div className="bg-gray-50 rounded-md p-3 text-sm space-y-1">
              <div><span className="text-gray-500">측정일: </span>{formatDate(deleteDialog.measurement.measured_at)}</div>
              <div><span className="text-gray-500">종목: </span>{getExerciseLabel(deleteDialog.measurement.exercise)}</div>
              {deleteDialog.measurement.score != null && (
                <div><span className="text-gray-500">점수: </span><span className="font-medium text-blue-600">{deleteDialog.measurement.score.toFixed(1)}점</span></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, measurement: null })}
              disabled={deletingMeasurement}
            >
              취소
            </Button>
            <Button
              onClick={handleDeleteMeasurement}
              disabled={deletingMeasurement}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-measurement-btn"
            >
              {deletingMeasurement ? '삭제 중...' : '삭제'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberDetail;
